import { ReactNode, memo, useEffect } from 'react';

import { useNavigate, useParams } from 'react-router';

import { useQueryPatient } from 'features/api';
import { useAuthentication, useParsedShareAccessToken } from 'features/auth';
import { PatientVerbiageProvider } from 'features/patient';

export const ShareInit = memo<{ children?: ReactNode }>(({ children }) => {
  const { activeScheme, logoutShare } = useAuthentication();
  const shareToken = useParsedShareAccessToken(true);
  const { linkId } = useParams<{ linkId: string }>();
  const navigate = useNavigate();

  const patientId = shareToken.sharePatientId;
  const [{ data: patient, isLoading: isLoadingPatient }] = useQueryPatient(patientId);

  // Handle linkId mismatch by triggering logout and redirect
  useEffect(() => {
    if (linkId !== shareToken.shareLinkId) {
      console.log('ShareInit: LinkId mismatch detected, logging out and redirecting');
      logoutShare();
      // Navigate to the correct share landing page
      const returnRoute = `${location.pathname}${location.search}${location.hash}`;
      navigate(`/share/${linkId}/landing?return=${encodeURIComponent(returnRoute)}`);
      return;
    }
  }, [linkId, shareToken.shareLinkId, logoutShare, navigate]);

  // This is the correct place to validate the linkId, after the RouteGuard has passed and context is stable.
  if (linkId !== shareToken.shareLinkId) {
    // This can happen in a race condition when switching between shares.
    // The useEffect above will handle the logout and redirect.
    return null;
  }

  // Only allow SHARE scheme
  if (activeScheme !== 'share') {
    console.warn('ShareInit: Active scheme is not share. RouteGuard should have handled this.');
    return null;
  }

  if (!shareToken.shareLinkId) {
    console.warn('ShareInit: No shareToken or shareLinkId for share scheme.');
    return null;
  }

  // Skip rendering if patient is still loading
  if (isLoadingPatient) {
    return null;
  }

  // Use patient's locationType directly instead of relying on session location
  const isOpo = patient?.locationType === 'OPO';

  return <PatientVerbiageProvider mode={isOpo ? 'Donor' : 'Patient'}>{children}</PatientVerbiageProvider>;
});

ShareInit.displayName = 'ShareInit';
